import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import "./updateLocalityPopup.css";
import {
    updateRentalLocality,
    getNearbyLocalities,
} from "../../Redux/Actions/rentalPropertiesActions";

/* ── Popup: manage localities for a rental property ──
   Uses only the two APIs already built:
     - GET  /rental-property/:id/nearby-localities  (fetched once on open)
     - PUT  /rental-property/update-locality/:id     (save, action: "set")
   The search box does NOT hit the server — it filters the nearby list
   that's already in memory, since nearby-localities is the only source
   of locality data the frontend has right now.
*/
const UpdateLocalityPopup = ({ isOpen, onClose, propertyId, existingLocalities }) => {
    const dispatch = useDispatch();

    const { nearbyLocalitiesList, localityLoading } = useSelector((state) => ({
        nearbyLocalitiesList: state.propertyRental?.nearbyLocalitiesList,
        localityLoading: state.propertyRental?.localityLoading,
    }));

    const [searchTerm, setSearchTerm] = useState("");
    const [selected, setSelected] = useState([]); // array of { _id, name, is_major, formatted_address }
    const [saving, setSaving] = useState(false);

    /* seed selection from whatever the property already has, every time the popup opens */
    useEffect(() => {
        if (isOpen) {
            setSelected(Array.isArray(existingLocalities) ? existingLocalities : []);
            setSearchTerm("");
            dispatch(getNearbyLocalities(propertyId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, propertyId]);

    /* client-side filter over the already-fetched nearby list — no extra API call */
    const displayList = useMemo(() => {
        const list = Array.isArray(nearbyLocalitiesList) ? nearbyLocalitiesList : [];
        const term = searchTerm.trim().toLowerCase();
        if (!term) return list;
        return list.filter((l) => l.name?.toLowerCase().includes(term));
    }, [nearbyLocalitiesList, searchTerm]);

    const listLoading = localityLoading;

    const isSelected = useCallback(
        (id) => selected.some((l) => l._id === id),
        [selected]
    );

    const toggleLocality = (locality) => {
        setSelected((prev) =>
            isSelected(locality._id)
                ? prev.filter((l) => l._id !== locality._id)
                : [...prev, locality]
        );
    };

    const removeSelected = (id) => {
        setSelected((prev) => prev.filter((l) => l._id !== id));
    };

    const handleSave = async () => {
        if (selected.length === 0) {
            toast.error("Select at least one locality");
            return;
        }
        setSaving(true);
        try {
            const res = await dispatch(
                updateRentalLocality(propertyId, {
                    localities: selected.map((l) => l._id),
                    action: "set",
                })
            );
            if (res?.statusCode === 200) {
                onClose();
            }
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="ulp-overlay" onClick={onClose}>
            <div className="ulp-modal" onClick={(e) => e.stopPropagation()}>
                <div className="ulp-header">
                    <h3 className="ulp-title">Manage Localities</h3>
                    <button className="ulp-close-btn" onClick={onClose} aria-label="Close">
                        ✕
                    </button>
                </div>

                <div className="ulp-search-wrap">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    <input
                        type="text"
                        className="ulp-search-input"
                        placeholder="Filter nearby localities by name…"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                        <button className="ulp-clear-btn" onClick={() => setSearchTerm("")}>
                            ✕
                        </button>
                    )}
                </div>

                {selected.length > 0 && (
                    <div className="ulp-selected-chips">
                        {selected.map((l) => (
                            <span key={l._id} className="ulp-chip">
                                {l.name}
                                <button
                                    className="ulp-chip-remove"
                                    onClick={() => removeSelected(l._id)}
                                    aria-label={`Remove ${l.name}`}
                                >
                                    ✕
                                </button>
                            </span>
                        ))}
                    </div>
                )}

                <div className="ulp-list-label">
                    Within 10km of this property
                </div>

                <div className="ulp-list">
                    {listLoading && (
                        <div className="ulp-empty-state">Loading…</div>
                    )}

                    {!listLoading && (!displayList || displayList.length === 0) && (
                        <div className="ulp-empty-state">
                            {searchTerm.trim()
                                ? "No nearby localities match that search."
                                : "No localities found nearby."}
                        </div>
                    )}

                    {!listLoading &&
                        displayList?.map((locality) => (
                            <label key={locality._id} className="ulp-list-item">
                                <input
                                    type="checkbox"
                                    checked={isSelected(locality._id)}
                                    onChange={() => toggleLocality(locality)}
                                />
                                <div className="ulp-list-item-text">
                                    <span className="ulp-list-item-name">
                                        {locality.name}
                                        {locality.is_major && <span className="ulp-major-badge">Major</span>}
                                    </span>
                                    <span className="ulp-list-item-sub">
                                        {locality.formatted_address || locality.short_address || ""}
                                        {locality.distance_km != null && ` · ${locality.distance_km} km away`}
                                    </span>
                                </div>
                            </label>
                        ))}
                </div>

                <div className="ulp-footer">
                    <button className="ulp-btn ulp-btn--ghost" onClick={onClose} disabled={saving}>
                        Cancel
                    </button>
                    <button className="ulp-btn ulp-btn--primary" onClick={handleSave} disabled={saving}>
                        {saving ? "Saving…" : `Save (${selected.length})`}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UpdateLocalityPopup;